import { resolveHistoryTimezone } from '../../common/historyTimezone';
import { apiContainer } from '../apiSlice/apiContainer';
import {
  DASHBOARD_API_ROOT,
  DASHBOARD_API_VERSION,
  DASHBOARD_TAG_TYPES
} from './dashboardContract';

const hasOwn = (value, property) =>
  Object.prototype.hasOwnProperty.call(value, property);

export const unwrapDashboardResponse = (response) => {
  if (!response || typeof response !== 'object') return response;

  const data = hasOwn(response, 'data') ? response.data : response;
  if (
    response.meta &&
    data &&
    typeof data === 'object' &&
    !Array.isArray(data)
  ) {
    return { ...data, meta: response.meta };
  }

  return data;
};

export const normalizeDashboardApiError = (response) => {
  const errors = response?.data?.errors;

  return {
    status: response?.status ?? 'UNKNOWN_ERROR',
    apiVersion: response?.data?.apiVersion ?? DASHBOARD_API_VERSION,
    errors:
      Array.isArray(errors) && errors.length > 0
        ? errors
        : [
            {
              code: 'dashboard_request_failed',
              message: 'The dashboard request could not be completed.',
              field: null,
              details: {}
            }
          ]
  };
};

export const getDashboardErrorMessage = (
  error,
  fallback = 'The dashboard request could not be completed.'
) => error?.errors?.[0]?.message || fallback;

const responseOptions = {
  transformResponse: unwrapDashboardResponse,
  transformErrorResponse: normalizeDashboardApiError
};

const templateUrl = (dashboardTemplateId) =>
  `${DASHBOARD_API_ROOT}/templates/${dashboardTemplateId}`;

const assignmentUrl = (dashboardTemplateId) =>
  `${templateUrl(dashboardTemplateId)}/assignments`;

const draftUrl = (dashboardTemplateId) =>
  `${templateUrl(dashboardTemplateId)}/draft`;

const metricUrl = (dashboardTemplateId, metricDefinitionId) =>
  `${draftUrl(dashboardTemplateId)}/metrics/${metricDefinitionId}`;

const visualizationUrl = (dashboardTemplateId, dashboardVisualizationId) =>
  `${draftUrl(dashboardTemplateId)}/visualizations/${dashboardVisualizationId}`;

const widgetUrl = (dashboardTemplateId, dashboardWidgetId) =>
  `${draftUrl(dashboardTemplateId)}/widgets/${dashboardWidgetId}`;

const templateInvalidation = (dashboardTemplateId) => [
  { type: 'DashboardTemplate', id: dashboardTemplateId },
  { type: 'DashboardTemplate', id: 'LIST' }
];

const effectiveInvalidation = [
  { type: 'DashboardEffective' },
  { type: 'DashboardData' }
];

const DEFAULT_DASHBOARD_IMPORT_TEMPLATE_FILENAME =
  'dashboard-data-import-template.csv';

export const getDashboardImportTemplateFilename = (contentDisposition) => {
  const match = /filename="?([^";]+)"?/i.exec(contentDisposition ?? '');
  const candidate = match?.[1]?.trim().split(/[\\/]/).pop();

  return candidate && candidate.toLowerCase().endsWith('.csv')
    ? candidate
    : DEFAULT_DASHBOARD_IMPORT_TEMPLATE_FILENAME;
};

const dashboardImportTemplateResponse = async (response) => {
  const contentType = response.headers.get('content-type') ?? '';
  if (!response.ok && contentType.includes('application/json')) {
    return response.json();
  }

  return response.text();
};

const dashboardImportTemplateTransform = (content, meta) => ({
  content,
  filename: getDashboardImportTemplateFilename(
    meta?.response?.headers.get('content-disposition')
  ),
  dashboardTemplateId:
    meta?.response?.headers.get('x-dashboard-template-id') ?? null,
  dashboardVersionId:
    meta?.response?.headers.get('x-dashboard-version-id') ?? null
});

const feedbackTags = (result, locationId) => [
  { type: 'DashboardFeedback', id: `LOCATION-${locationId}` },
  ...(result?.feedback?.items ?? []).map((item) => ({
    type: 'DashboardFeedback',
    id: item.feedbackId
  }))
];

export const buildDashboardEndpoints = (build) => ({
  getEffectiveDashboard: build.query({
    query: (locationId) => ({
      url: `${DASHBOARD_API_ROOT}/effective`,
      params: { locationId }
    }),
    ...responseOptions,
    providesTags: (_result, _error, locationId) => [
      { type: 'DashboardEffective', id: locationId }
    ]
  }),

  listDashboardTemplates: build.query({
    query: () => `${DASHBOARD_API_ROOT}/templates`,
    ...responseOptions,
    providesTags: (result) => [
      { type: 'DashboardTemplate', id: 'LIST' },
      ...(result?.templates ?? []).map((template) => ({
        type: 'DashboardTemplate',
        id: template.dashboardTemplateId
      }))
    ]
  }),

  listDeletedDashboardTemplates: build.query({
    query: () => `${DASHBOARD_API_ROOT}/templates/deleted`,
    ...responseOptions,
    providesTags: (result) => [
      { type: 'DashboardTemplate', id: 'DELETED' },
      ...(result?.templates ?? []).map((template) => ({
        type: 'DashboardTemplate',
        id: template.dashboardTemplateId
      }))
    ]
  }),

  createDashboardTemplate: build.mutation({
    query: (body) => ({
      url: `${DASHBOARD_API_ROOT}/templates`,
      method: 'POST',
      body
    }),
    ...responseOptions,
    invalidatesTags: [{ type: 'DashboardTemplate', id: 'LIST' }]
  }),

  getDashboardTemplate: build.query({
    query: (dashboardTemplateId) => templateUrl(dashboardTemplateId),
    ...responseOptions,
    providesTags: (_result, _error, dashboardTemplateId) => [
      { type: 'DashboardTemplate', id: dashboardTemplateId }
    ]
  }),

  getDashboardAssignments: build.query({
    query: (dashboardTemplateId) => assignmentUrl(dashboardTemplateId),
    ...responseOptions,
    providesTags: (_result, _error, dashboardTemplateId) => [
      { type: 'DashboardAssignment', id: dashboardTemplateId }
    ]
  }),

  replaceDashboardAssignments: build.mutation({
    query: ({ dashboardTemplateId, ...body }) => ({
      url: assignmentUrl(dashboardTemplateId),
      method: 'PUT',
      body
    }),
    ...responseOptions,
    invalidatesTags: [
      { type: 'DashboardAssignment' },
      { type: 'DashboardTemplate' },
      ...effectiveInvalidation
    ]
  }),

  updateDashboardDraft: build.mutation({
    query: ({ dashboardTemplateId, ...body }) => ({
      url: draftUrl(dashboardTemplateId),
      method: 'PATCH',
      body
    }),
    ...responseOptions,
    invalidatesTags: (_result, _error, { dashboardTemplateId }) =>
      templateInvalidation(dashboardTemplateId)
  }),

  publishDashboardDraft: build.mutation({
    query: ({ dashboardTemplateId, ...body }) => ({
      url: `${templateUrl(dashboardTemplateId)}/publish`,
      method: 'POST',
      body
    }),
    ...responseOptions,
    invalidatesTags: (_result, _error, { dashboardTemplateId }) => [
      ...templateInvalidation(dashboardTemplateId),
      { type: 'DashboardAssignment' },
      ...effectiveInvalidation
    ]
  }),

  archiveDashboardTemplate: build.mutation({
    query: (dashboardTemplateId) => ({
      url: `${templateUrl(dashboardTemplateId)}/archive`,
      method: 'POST'
    }),
    ...responseOptions,
    invalidatesTags: (_result, _error, dashboardTemplateId) => [
      ...templateInvalidation(dashboardTemplateId),
      { type: 'DashboardAssignment' },
      ...effectiveInvalidation
    ]
  }),

  restoreDashboardTemplate: build.mutation({
    query: (dashboardTemplateId) => ({
      url: `${templateUrl(dashboardTemplateId)}/restore`,
      method: 'POST'
    }),
    ...responseOptions,
    invalidatesTags: (_result, _error, dashboardTemplateId) => [
      ...templateInvalidation(dashboardTemplateId),
      { type: 'DashboardAssignment' },
      ...effectiveInvalidation
    ]
  }),

  deleteDashboardTemplate: build.mutation({
    query: ({ dashboardTemplateId, reason }) => ({
      url: templateUrl(dashboardTemplateId),
      method: 'DELETE',
      body: { reason }
    }),
    ...responseOptions,
    invalidatesTags: (_result, _error, { dashboardTemplateId }) => [
      ...templateInvalidation(dashboardTemplateId),
      { type: 'DashboardTemplate', id: 'DELETED' },
      { type: 'DashboardAssignment' },
      ...effectiveInvalidation
    ]
  }),

  restoreDeletedDashboardTemplate: build.mutation({
    query: (dashboardTemplateId) => ({
      url: `${templateUrl(dashboardTemplateId)}/restore-deleted`,
      method: 'POST'
    }),
    ...responseOptions,
    invalidatesTags: (_result, _error, dashboardTemplateId) => [
      ...templateInvalidation(dashboardTemplateId),
      { type: 'DashboardTemplate', id: 'DELETED' },
      { type: 'DashboardAssignment' },
      ...effectiveInvalidation
    ]
  }),

  createDashboardMetric: build.mutation({
    query: ({ dashboardTemplateId, ...body }) => ({
      url: `${draftUrl(dashboardTemplateId)}/metrics`,
      method: 'POST',
      body
    }),
    ...responseOptions,
    invalidatesTags: (_result, _error, { dashboardTemplateId }) =>
      templateInvalidation(dashboardTemplateId)
  }),

  updateDashboardMetric: build.mutation({
    query: ({ dashboardTemplateId, metricDefinitionId, ...body }) => ({
      url: metricUrl(dashboardTemplateId, metricDefinitionId),
      method: 'PATCH',
      body
    }),
    ...responseOptions,
    invalidatesTags: (_result, _error, { dashboardTemplateId }) =>
      templateInvalidation(dashboardTemplateId)
  }),

  archiveDashboardMetric: build.mutation({
    query: ({ dashboardTemplateId, metricDefinitionId }) => ({
      url: `${metricUrl(dashboardTemplateId, metricDefinitionId)}/archive`,
      method: 'POST'
    }),
    ...responseOptions,
    invalidatesTags: (_result, _error, { dashboardTemplateId }) =>
      templateInvalidation(dashboardTemplateId)
  }),

  restoreDashboardMetric: build.mutation({
    query: ({ dashboardTemplateId, metricDefinitionId }) => ({
      url: `${metricUrl(dashboardTemplateId, metricDefinitionId)}/restore`,
      method: 'POST'
    }),
    ...responseOptions,
    invalidatesTags: (_result, _error, { dashboardTemplateId }) =>
      templateInvalidation(dashboardTemplateId)
  }),

  reorderDashboardMetrics: build.mutation({
    query: ({ dashboardTemplateId, metricDefinitionIds }) => ({
      url: `${draftUrl(dashboardTemplateId)}/metric-order`,
      method: 'PUT',
      body: { metricDefinitionIds }
    }),
    ...responseOptions,
    invalidatesTags: (_result, _error, { dashboardTemplateId }) =>
      templateInvalidation(dashboardTemplateId)
  }),

  createDashboardVisualization: build.mutation({
    query: ({ dashboardTemplateId, ...body }) => ({
      url: `${draftUrl(dashboardTemplateId)}/visualizations`,
      method: 'POST',
      body
    }),
    ...responseOptions,
    invalidatesTags: (_result, _error, { dashboardTemplateId }) =>
      templateInvalidation(dashboardTemplateId)
  }),

  updateDashboardVisualization: build.mutation({
    query: ({ dashboardTemplateId, dashboardVisualizationId, ...body }) => ({
      url: visualizationUrl(dashboardTemplateId, dashboardVisualizationId),
      method: 'PATCH',
      body
    }),
    ...responseOptions,
    invalidatesTags: (_result, _error, { dashboardTemplateId }) =>
      templateInvalidation(dashboardTemplateId)
  }),

  deleteDashboardVisualization: build.mutation({
    query: ({ dashboardTemplateId, dashboardVisualizationId }) => ({
      url: visualizationUrl(dashboardTemplateId, dashboardVisualizationId),
      method: 'DELETE'
    }),
    ...responseOptions,
    invalidatesTags: (_result, _error, { dashboardTemplateId }) =>
      templateInvalidation(dashboardTemplateId)
  }),

  replaceDashboardVisualizationSeries: build.mutation({
    query: ({ dashboardTemplateId, dashboardVisualizationId, series }) => ({
      url: `${visualizationUrl(
        dashboardTemplateId,
        dashboardVisualizationId
      )}/series`,
      method: 'PUT',
      body: { series }
    }),
    ...responseOptions,
    invalidatesTags: (_result, _error, { dashboardTemplateId }) =>
      templateInvalidation(dashboardTemplateId)
  }),

  reorderDashboardVisualizations: build.mutation({
    query: ({ dashboardTemplateId, dashboardVisualizationIds }) => ({
      url: `${draftUrl(dashboardTemplateId)}/visualization-order`,
      method: 'PUT',
      body: { dashboardVisualizationIds }
    }),
    ...responseOptions,
    invalidatesTags: (_result, _error, { dashboardTemplateId }) =>
      templateInvalidation(dashboardTemplateId)
  }),

  createDashboardWidget: build.mutation({
    query: ({ dashboardTemplateId, ...body }) => ({
      url: `${draftUrl(dashboardTemplateId)}/widgets`,
      method: 'POST',
      body
    }),
    ...responseOptions,
    invalidatesTags: (_result, _error, { dashboardTemplateId }) =>
      templateInvalidation(dashboardTemplateId)
  }),

  updateDashboardWidget: build.mutation({
    query: ({ dashboardTemplateId, dashboardWidgetId, ...body }) => ({
      url: widgetUrl(dashboardTemplateId, dashboardWidgetId),
      method: 'PATCH',
      body
    }),
    ...responseOptions,
    invalidatesTags: (_result, _error, { dashboardTemplateId }) =>
      templateInvalidation(dashboardTemplateId)
  }),

  deleteDashboardWidget: build.mutation({
    query: ({ dashboardTemplateId, dashboardWidgetId }) => ({
      url: widgetUrl(dashboardTemplateId, dashboardWidgetId),
      method: 'DELETE'
    }),
    ...responseOptions,
    invalidatesTags: (_result, _error, { dashboardTemplateId }) =>
      templateInvalidation(dashboardTemplateId)
  }),

  replaceDashboardWidgetMetrics: build.mutation({
    query: ({ dashboardTemplateId, dashboardWidgetId, metrics }) => ({
      url: `${widgetUrl(dashboardTemplateId, dashboardWidgetId)}/metrics`,
      method: 'PUT',
      body: { metrics }
    }),
    ...responseOptions,
    invalidatesTags: (_result, _error, { dashboardTemplateId }) =>
      templateInvalidation(dashboardTemplateId)
  }),

  reorderDashboardWidgets: build.mutation({
    query: ({ dashboardTemplateId, dashboardWidgetIds }) => ({
      url: `${draftUrl(dashboardTemplateId)}/widget-order`,
      method: 'PUT',
      body: { dashboardWidgetIds }
    }),
    ...responseOptions,
    invalidatesTags: (_result, _error, { dashboardTemplateId }) =>
      templateInvalidation(dashboardTemplateId)
  }),

  getDashboardLatest: build.query({
    query: (locationId) =>
      `${DASHBOARD_API_ROOT}/locations/${locationId}/latest`,
    ...responseOptions,
    providesTags: (_result, _error, locationId) => [
      { type: 'DashboardData', id: locationId }
    ]
  }),

  getDashboardWidgets: build.query({
    query: (locationId) =>
      `${DASHBOARD_API_ROOT}/locations/${locationId}/widgets`,
    ...responseOptions,
    providesTags: (_result, _error, locationId) => [
      { type: 'DashboardData', id: locationId }
    ]
  }),

  getDashboardSeries: build.query({
    query: ({ locationId, fromDate, toDate, timezone }) => ({
      url: `${DASHBOARD_API_ROOT}/locations/${locationId}/series`,
      params: { fromDate, toDate, timezone: resolveHistoryTimezone(timezone) }
    }),
    ...responseOptions,
    providesTags: (_result, _error, { locationId }) => [
      { type: 'DashboardData', id: locationId }
    ]
  }),

  getDashboardArchivedSeries: build.query({
    query: ({ locationId, fromDate, toDate, timezone }) => ({
      url: `${DASHBOARD_API_ROOT}/locations/${locationId}/archived-metrics/series`,
      params: { fromDate, toDate, timezone: resolveHistoryTimezone(timezone) }
    }),
    ...responseOptions,
    providesTags: (_result, _error, { locationId }) => [
      { type: 'DashboardData', id: locationId }
    ]
  }),

  createDashboardSubmission: build.mutation({
    query: ({ locationId, ...body }) => ({
      url: `${DASHBOARD_API_ROOT}/locations/${locationId}/submissions`,
      method: 'POST',
      body
    }),
    ...responseOptions,
    invalidatesTags: (result, _error, { locationId }) => [
      { type: 'DashboardData', id: locationId },
      { type: 'DashboardReview', id: 'LIST' },
      ...(result?.submissionId
        ? [{ type: 'DashboardSubmission', id: result.submissionId }]
        : [])
    ]
  }),

  downloadDashboardImportTemplate: build.mutation({
    query: (locationId) => ({
      url: `${DASHBOARD_API_ROOT}/locations/${locationId}/import-template`,
      method: 'GET',
      responseHandler: dashboardImportTemplateResponse
    }),
    transformResponse: dashboardImportTemplateTransform,
    transformErrorResponse: normalizeDashboardApiError
  }),

  importDashboardCsv: build.mutation({
    query: ({ locationId, file, approverUserId }) => {
      const body = new FormData();
      body.append('file', file);
      body.append('approverUserId', approverUserId);
      return {
        url: `${DASHBOARD_API_ROOT}/locations/${locationId}/imports`,
        method: 'POST',
        body
      };
    },
    ...responseOptions,
    invalidatesTags: (result, _error, { locationId }) =>
      result?.summary?.importedRows
        ? [
            { type: 'DashboardData', id: locationId },
            { type: 'DashboardReview', id: 'LIST' },
            ...(result.rows ?? [])
              .filter((row) => row.submissionId)
              .map((row) => ({
                type: 'DashboardSubmission',
                id: row.submissionId
              }))
          ]
        : []
  }),

  getDashboardSubmission: build.query({
    query: (submissionId) =>
      `${DASHBOARD_API_ROOT}/submissions/${submissionId}`,
    ...responseOptions,
    providesTags: (_result, _error, submissionId) => [
      { type: 'DashboardSubmission', id: submissionId }
    ]
  }),

  listDashboardPendingReviews: build.query({
    query: ({ page = 1, pageSize = 25 } = {}) => ({
      url: `${DASHBOARD_API_ROOT}/metric-value-revisions/pending`,
      params: { page, pageSize }
    }),
    ...responseOptions,
    providesTags: (result) => [
      { type: 'DashboardReview', id: 'LIST' },
      ...(result?.pendingReviews ?? []).map((review) => ({
        type: 'DashboardReview',
        id: review.metricValueRevisionId
      }))
    ]
  }),

  createDashboardMetricValueRevision: build.mutation({
    query: ({ metricValueId, value, approverUserId }) => ({
      url: `${DASHBOARD_API_ROOT}/metric-values/${metricValueId}`,
      method: 'PUT',
      body: { value, approverUserId }
    }),
    ...responseOptions,
    invalidatesTags: [
      { type: 'DashboardData' },
      { type: 'DashboardSubmission' },
      { type: 'DashboardReview', id: 'LIST' }
    ]
  }),

  approveDashboardMetricValueRevision: build.mutation({
    query: ({ metricValueRevisionId, reviewNote = null }) => ({
      url: `${DASHBOARD_API_ROOT}/metric-value-revisions/${metricValueRevisionId}/approve`,
      method: 'POST',
      body: { reviewNote }
    }),
    ...responseOptions,
    invalidatesTags: [
      { type: 'DashboardData' },
      { type: 'DashboardSubmission' },
      { type: 'DashboardReview', id: 'LIST' }
    ]
  }),

  rejectDashboardMetricValueRevision: build.mutation({
    query: ({ metricValueRevisionId, rejectionReason }) => ({
      url: `${DASHBOARD_API_ROOT}/metric-value-revisions/${metricValueRevisionId}/reject`,
      method: 'POST',
      body: { rejectionReason }
    }),
    ...responseOptions,
    invalidatesTags: [
      { type: 'DashboardData' },
      { type: 'DashboardSubmission' },
      { type: 'DashboardReview', id: 'LIST' }
    ]
  }),

  deleteDashboardMetricValue: build.mutation({
    query: ({ metricValueId, reason }) => ({
      url: `${DASHBOARD_API_ROOT}/metric-values/${metricValueId}`,
      method: 'DELETE',
      body: { reason }
    }),
    ...responseOptions,
    invalidatesTags: [
      { type: 'DashboardData' },
      { type: 'DashboardSubmission' },
      { type: 'DashboardReview', id: 'LIST' }
    ]
  }),

  restoreDashboardMetricValue: build.mutation({
    query: (metricValueId) => ({
      url: `${DASHBOARD_API_ROOT}/metric-values/${metricValueId}/restore`,
      method: 'POST'
    }),
    ...responseOptions,
    invalidatesTags: [
      { type: 'DashboardData' },
      { type: 'DashboardSubmission' },
      { type: 'DashboardReview', id: 'LIST' }
    ]
  }),

  listDashboardFeedback: build.query({
    query: ({ locationId, page = 1, pageSize = 50 }) => ({
      url: `${DASHBOARD_API_ROOT}/locations/${locationId}/feedback`,
      params: { page, pageSize }
    }),
    ...responseOptions,
    providesTags: (result, _error, { locationId }) =>
      feedbackTags(result, locationId)
  }),

  createDashboardFeedback: build.mutation({
    query: ({ locationId, ...body }) => ({
      url: `${DASHBOARD_API_ROOT}/locations/${locationId}/feedback`,
      method: 'POST',
      body
    }),
    ...responseOptions,
    invalidatesTags: (_result, _error, { locationId }) => [
      { type: 'DashboardFeedback', id: `LOCATION-${locationId}` }
    ]
  }),

  getDashboardFeedback: build.query({
    query: (feedbackId) => `${DASHBOARD_API_ROOT}/feedback/${feedbackId}`,
    ...responseOptions,
    providesTags: (_result, _error, feedbackId) => [
      { type: 'DashboardFeedback', id: feedbackId }
    ]
  }),

  updateDashboardFeedback: build.mutation({
    query: ({ feedbackId, ...body }) => ({
      url: `${DASHBOARD_API_ROOT}/feedback/${feedbackId}`,
      method: 'PATCH',
      body
    }),
    ...responseOptions,
    invalidatesTags: (_result, _error, { feedbackId }) => [
      { type: 'DashboardFeedback', id: feedbackId },
      { type: 'DashboardFeedback' }
    ]
  }),

  transitionDashboardFeedback: build.mutation({
    query: ({ feedbackId, feedbackStatus, resolutionNotes = null }) => ({
      url: `${DASHBOARD_API_ROOT}/feedback/${feedbackId}/status`,
      method: 'POST',
      body: { feedbackStatus, resolutionNotes }
    }),
    ...responseOptions,
    invalidatesTags: (_result, _error, { feedbackId }) => [
      { type: 'DashboardFeedback', id: feedbackId },
      { type: 'DashboardFeedback' }
    ]
  }),

  deleteDashboardFeedback: build.mutation({
    query: ({ feedbackId, reason, approverUserId }) => ({
      url: `${DASHBOARD_API_ROOT}/feedback/${feedbackId}`,
      method: 'DELETE',
      body: { reason, approverUserId }
    }),
    ...responseOptions,
    invalidatesTags: (_result, _error, { feedbackId }) => [
      { type: 'DashboardFeedback', id: feedbackId },
      { type: 'DashboardFeedback' }
    ]
  }),

  restoreDashboardFeedback: build.mutation({
    query: (feedbackId) => ({
      url: `${DASHBOARD_API_ROOT}/feedback/${feedbackId}/restore`,
      method: 'POST'
    }),
    ...responseOptions,
    invalidatesTags: (_result, _error, feedbackId) => [
      { type: 'DashboardFeedback', id: feedbackId },
      { type: 'DashboardFeedback' }
    ]
  })
});

const dashboardApi = apiContainer
  .enhanceEndpoints({ addTagTypes: DASHBOARD_TAG_TYPES })
  .injectEndpoints({
    endpoints: buildDashboardEndpoints,
    overrideExisting: false
  });

export const {
  useGetEffectiveDashboardQuery,
  useLazyGetEffectiveDashboardQuery,
  useListDashboardTemplatesQuery,
  useLazyListDashboardTemplatesQuery,
  useListDeletedDashboardTemplatesQuery,
  useLazyListDeletedDashboardTemplatesQuery,
  useCreateDashboardTemplateMutation,
  useGetDashboardTemplateQuery,
  useLazyGetDashboardTemplateQuery,
  useGetDashboardAssignmentsQuery,
  useLazyGetDashboardAssignmentsQuery,
  useReplaceDashboardAssignmentsMutation,
  useUpdateDashboardDraftMutation,
  usePublishDashboardDraftMutation,
  useArchiveDashboardTemplateMutation,
  useRestoreDashboardTemplateMutation,
  useDeleteDashboardTemplateMutation,
  useRestoreDeletedDashboardTemplateMutation,
  useCreateDashboardMetricMutation,
  useUpdateDashboardMetricMutation,
  useArchiveDashboardMetricMutation,
  useRestoreDashboardMetricMutation,
  useReorderDashboardMetricsMutation,
  useCreateDashboardVisualizationMutation,
  useUpdateDashboardVisualizationMutation,
  useDeleteDashboardVisualizationMutation,
  useReplaceDashboardVisualizationSeriesMutation,
  useReorderDashboardVisualizationsMutation,
  useCreateDashboardWidgetMutation,
  useUpdateDashboardWidgetMutation,
  useDeleteDashboardWidgetMutation,
  useReplaceDashboardWidgetMetricsMutation,
  useReorderDashboardWidgetsMutation,
  useGetDashboardLatestQuery,
  useLazyGetDashboardLatestQuery,
  useGetDashboardWidgetsQuery,
  useLazyGetDashboardWidgetsQuery,
  useGetDashboardSeriesQuery,
  useLazyGetDashboardSeriesQuery,
  useGetDashboardArchivedSeriesQuery,
  useLazyGetDashboardArchivedSeriesQuery,
  useCreateDashboardSubmissionMutation,
  useDownloadDashboardImportTemplateMutation,
  useImportDashboardCsvMutation,
  useGetDashboardSubmissionQuery,
  useLazyGetDashboardSubmissionQuery,
  useListDashboardPendingReviewsQuery,
  useLazyListDashboardPendingReviewsQuery,
  useCreateDashboardMetricValueRevisionMutation,
  useApproveDashboardMetricValueRevisionMutation,
  useRejectDashboardMetricValueRevisionMutation,
  useDeleteDashboardMetricValueMutation,
  useRestoreDashboardMetricValueMutation,
  useListDashboardFeedbackQuery,
  useLazyListDashboardFeedbackQuery,
  useCreateDashboardFeedbackMutation,
  useGetDashboardFeedbackQuery,
  useLazyGetDashboardFeedbackQuery,
  useUpdateDashboardFeedbackMutation,
  useTransitionDashboardFeedbackMutation,
  useDeleteDashboardFeedbackMutation,
  useRestoreDashboardFeedbackMutation
} = dashboardApi;

export default dashboardApi;
